import { useEffect, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import ReactMarkdown from "react-markdown";
import { toast, Toaster } from "sonner";
import { ArrowLeft, ArrowUpRight, Instagram, Link2, Share2 } from "lucide-react";
import { api, resolveImg } from "@/lib/api";

const fmtDate = (iso) =>
  new Date(iso).toLocaleDateString("es-AR", { day: "2-digit", month: "long", year: "numeric" });

const mdComponents = {
  h2: ({ node, ...p }) => <h2 className="font-display uppercase text-3xl sm:text-4xl tracking-tight mt-14 mb-5" {...p} />,
  h3: ({ node, ...p }) => <h3 className="font-display uppercase text-2xl tracking-tight mt-10 mb-4 text-[var(--yellow)]" {...p} />,
  p: ({ node, ...p }) => <p className="text-lg leading-relaxed text-neutral-300 mb-6" {...p} />,
  ul: ({ node, ...p }) => <ul className="list-disc pl-6 space-y-3 mb-6 text-lg text-neutral-300" {...p} />,
  ol: ({ node, ...p }) => <ol className="list-decimal pl-6 space-y-3 mb-6 text-lg text-neutral-300" {...p} />,
  li: ({ node, ...p }) => <li className="leading-relaxed" {...p} />,
  strong: ({ node, ...p }) => <strong className="text-white font-semibold" {...p} />,
  a: ({ node, ...p }) => <a className="text-[var(--fuchsia)] underline underline-offset-4" {...p} />,
};

const WhatsappIcon = ({ size = 16 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.501-.669-.51l-.57-.01c-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.626.712.226 1.36.194 1.872.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z" />
  </svg>
);

const ShareBar = ({ title }) => {
  const url = typeof window !== "undefined" ? window.location.href : "";
  const shareText = `${title} — Distópica`;

  const whatsapp = () =>
    window.open(`https://wa.me/?text=${encodeURIComponent(`${shareText} ${url}`)}`, "_blank", "noopener");

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(url);
      toast.success("¡Enlace copiado! Pegalo en tu historia de Instagram.");
    } catch {
      toast.error("No se pudo copiar el enlace");
    }
  };

  const instagram = () => {
    copyLink();
    window.open("https://instagram.com/distopica.tienda", "_blank", "noopener");
  };

  const native = async () => {
    if (navigator.share) {
      try {
        await navigator.share({ title, text: shareText, url });
      } catch {}
    } else {
      copyLink();
    }
  };

  const btn =
    "inline-flex items-center gap-2 border border-white/20 px-4 py-2.5 rounded-sm font-mono-x text-xs uppercase tracking-[0.15em] transition-colors";

  return (
    <div className="flex flex-wrap items-center gap-3" data-testid="share-bar">
      <span className="font-mono-x text-[11px] uppercase tracking-widest text-neutral-500 mr-1">Compartir</span>
      <button onClick={whatsapp} data-testid="share-whatsapp" className={`${btn} hover:border-[#25D366] hover:text-[#25D366]`} aria-label="Compartir en WhatsApp">
        <WhatsappIcon size={15} /> WhatsApp
      </button>
      <button onClick={instagram} data-testid="share-instagram" className={`${btn} hover:border-[var(--fuchsia)] hover:text-[var(--fuchsia)]`} aria-label="Compartir en Instagram">
        <Instagram size={15} /> Instagram
      </button>
      <button onClick={copyLink} data-testid="share-copy" className={`${btn} hover:border-[var(--yellow)] hover:text-[var(--yellow)]`} aria-label="Copiar enlace">
        <Link2 size={15} /> Copiar
      </button>
      <button onClick={native} data-testid="share-native" className={`${btn} hover:border-white hover:text-white sm:hidden`} aria-label="Compartir">
        <Share2 size={15} />
      </button>
    </div>
  );
};

export default function BlogPost() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [post, setPost] = useState(null);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    api
      .get(`/blog/posts/${slug}`)
      .then((r) => {
        setPost(r.data);
        document.title = `${r.data.title} | Distópica`;
        const meta = document.querySelector('meta[name="description"]');
        if (meta && r.data.excerpt) meta.setAttribute("content", r.data.excerpt);
      })
      .catch(() => setNotFound(true));
  }, [slug]);

  if (notFound) {
    return (
      <main className="bg-[#0A0A0A] min-h-screen flex flex-col items-center justify-center gap-6 px-6 text-center">
        <h1 className="font-display uppercase text-5xl">Artículo no encontrado</h1>
        <button onClick={() => navigate("/blog")} className="font-mono-x text-xs uppercase tracking-[0.2em] border border-white/30 px-6 py-3 rounded-sm hover:border-[var(--yellow)] hover:text-[var(--yellow)] transition-colors">
          Ver todos los artículos
        </button>
      </main>
    );
  }

  if (!post) {
    return (
      <main className="bg-[#0A0A0A] min-h-screen flex items-center justify-center">
        <p className="font-mono-x text-sm text-neutral-500">Cargando…</p>
      </main>
    );
  }

  return (
    <main className="bg-[#0A0A0A] min-h-screen">
      <Toaster position="top-center" theme="dark" richColors />
      <header className="border-b border-white/10 sticky top-0 z-40 backdrop-blur-xl bg-black/60">
        <div className="max-w-[1400px] mx-auto px-5 sm:px-8 h-16 flex items-center justify-between">
          <Link to="/" className="font-display text-2xl tracking-tight leading-none">
            DISTÓPICA<span style={{ color: "var(--fuchsia)" }}>.</span>
          </Link>
          <Link to="/blog" data-testid="post-back" className="font-mono-x text-xs uppercase tracking-[0.2em] text-neutral-300 hover:text-[var(--yellow)] transition-colors flex items-center gap-2">
            <ArrowLeft size={16} /> Blog
          </Link>
        </div>
      </header>

      <article className="max-w-3xl mx-auto px-5 sm:px-8 py-16 sm:py-24" data-testid="post-article">
        <div className="flex flex-wrap gap-2 mb-6">
          {(post.tags || []).map((t) => (
            <span key={t} className="patch text-[var(--fuchsia)] rotate-[-1deg]">{t}</span>
          ))}
        </div>
        <h1 className="font-display uppercase leading-[0.95] tracking-tight text-4xl sm:text-6xl mb-6">{post.title}</h1>
        <div className="flex items-center gap-4 font-mono-x text-xs uppercase tracking-widest text-neutral-500 mb-8">
          <span>{post.author}</span>
          <span className="w-8 h-px bg-white/20" />
          <span>{fmtDate(post.created_at)}</span>
        </div>

        <div className="mb-10 pb-8 border-b border-white/10">
          <ShareBar title={post.title} />
        </div>

        {post.cover_image && (
          <div className="border border-white/10 mb-12 overflow-hidden">
            <img src={resolveImg(post.cover_image)} alt={post.title} className="w-full object-cover" />
          </div>
        )}

        <div>
          <ReactMarkdown components={mdComponents}>{post.content}</ReactMarkdown>
        </div>

        <div className="mt-16 pt-10 border-t border-white/10 flex flex-col gap-8">
          <ShareBar title={post.title} />
          <Link to="/#contacto" className="group inline-flex items-center gap-2 bg-[var(--fuchsia)] text-white font-mono-x uppercase text-sm tracking-[0.15em] px-7 py-4 rounded-sm hover:bg-[var(--yellow)] hover:text-black transition-colors w-fit">
            Cotizá tu proyecto
            <ArrowUpRight size={18} className="group-hover:rotate-45 transition-transform" />
          </Link>
        </div>
      </article>
    </main>
  );
}
